import { drawEggClipPath, getEggDimensions } from './egg-shape';

/**
 * Composite egg template, optional photo, and drawing canvas into a single egg-shaped JPEG image.
 *
 * @param {HTMLCanvasElement} templateCanvas - The egg template canvas
 * @param {HTMLCanvasElement} drawingCanvas - The freehand drawing canvas
 * @param {Object|null} photoOptions - Optional photo layer data
 * @param {HTMLCanvasElement} photoOptions.canvas - The photo layer canvas
 * @param {string} photoOptions.photoData - Photo data URL
 * @param {Object} photoOptions.photoTransform - { x, y, scale }
 * @param {number} photoOptions.displayWidth - Display width of the egg container
 * @param {number} outputWidth - Desired output width (default 800)
 * @returns {string} Base64 JPEG data URL
 */
export function exportEggImage(
  templateCanvas,
  drawingCanvas,
  photoOptions = null,
  outputWidth = 800,
) {
  const aspectRatio = 5 / 4;
  const outputHeight = Math.round(outputWidth * aspectRatio);

  const composite = document.createElement('canvas');
  composite.width = outputWidth;
  composite.height = outputHeight;
  const ctx = composite.getContext('2d');

  // Apply egg clip mask
  const { cx, cy, rx, ry } = getEggDimensions(outputWidth, outputHeight);
  drawEggClipPath(ctx, cx, cy, rx, ry);
  ctx.clip();

  // Transparent background (PNG preserves transparency)

  const hasPhoto = photoOptions && photoOptions.canvas;

  // Layer 1: Egg template
  if (templateCanvas) {
    try {
      if (hasPhoto) {
        // Reduce template opacity when photo is present
        ctx.globalAlpha = 0.3;
      }
      ctx.drawImage(templateCanvas, 0, 0, outputWidth, outputHeight);
      ctx.globalAlpha = 1;
    } catch (e) {
      ctx.globalAlpha = 1;
      console.warn('Could not capture template canvas:', e);
    }
  }

  // Layer 2: Photo (if present)
  if (hasPhoto) {
    try {
      ctx.drawImage(photoOptions.canvas, 0, 0, outputWidth, outputHeight);
    } catch (e) {
      console.warn('Could not capture photo canvas:', e);
    }
  }

  // Layer 3: Drawing canvas
  if (drawingCanvas) {
    ctx.drawImage(drawingCanvas, 0, 0, outputWidth, outputHeight);
  }

  return composite.toDataURL('image/png');
}

/**
 * Compress a base64 image to ensure it's under the max size.
 *
 * @param {string} base64 - Base64 data URL
 * @param {number} maxSizeBytes - Maximum size in bytes (default 1MB)
 * @returns {string} Compressed base64 data URL
 */
export function compressIfNeeded(base64, maxSizeBytes = 1024 * 1024) {
  const estimatedSize = (base64.length - base64.indexOf(',') - 1) * 0.75;

  if (estimatedSize <= maxSizeBytes) {
    return base64;
  }

  const img = new Image();
  img.src = base64;

  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0);

  // PNG doesn't have quality parameter, so scale down dimensions instead
  for (let scale = 0.75; scale >= 0.4; scale -= 0.1) {
    const scaledCanvas = document.createElement('canvas');
    scaledCanvas.width = Math.round(img.width * scale);
    scaledCanvas.height = Math.round(img.height * scale);
    const scaledCtx = scaledCanvas.getContext('2d');
    scaledCtx.drawImage(img, 0, 0, scaledCanvas.width, scaledCanvas.height);
    const compressed = scaledCanvas.toDataURL('image/png');
    const size = (compressed.length - compressed.indexOf(',') - 1) * 0.75;
    if (size <= maxSizeBytes) {
      return compressed;
    }
  }

  return canvas.toDataURL('image/png');
}
